/** Index-preserving connectivity; no clean, triangulate, or decimation. */
export function adjacency(polys: ArrayLike<number>, pointCount: number) {
  const neighbors = Array.from({ length: pointCount }, () => new Set<number>());
  for (let offset = 0; offset < polys.length;) {
    const count = polys[offset++];
    if (count < 3 || offset + count > polys.length) throw new Error('Invalid polygon connectivity.');
    const face = Array.from({ length: count }, () => polys[offset++]);
    if (face.some((id) => !Number.isInteger(id) || id < 0 || id >= pointCount)) throw new Error('Mesh point index is out of bounds.');
    face.forEach((a, i) => { const b = face[(i + 1) % count]; neighbors[a].add(b); neighbors[b].add(a); });
  }
  return neighbors.map((set) => [...set]);
}

/** Geodesic brush travels only connected edges. Nearby disconnected teeth stay untouched. */
export function connectedBrush(points: ArrayLike<number>, graph: number[][], seed: number, radiusMm: number,
  gate?: { normals: ArrayLike<number>; maxAngleDegrees: number }) {
  if (!Number.isInteger(seed) || seed < 0 || seed >= graph.length || !Number.isFinite(radiusMm) || radiusMm < 0) throw new Error('Invalid brush seed/radius.');
  const distance = new Map<number, number>([[seed, 0]]);
  const pending = [seed];
  const result: number[] = [];
  const visited = new Set<number>();
  while (pending.length) {
    // Local neighborhoods are small; no full-mesh spatial index per stroke.
    pending.sort((a, b) => distance.get(b)! - distance.get(a)!);
    const id = pending.pop()!;
    if (visited.has(id)) continue;
    visited.add(id); result.push(id);
    for (const next of graph[id]) {
      if (gate && gate.maxAngleDegrees < 180) {
        const dot = [0, 1, 2].reduce((sum, axis) => sum + gate.normals[seed * 3 + axis] * gate.normals[next * 3 + axis], 0);
        if (dot < Math.cos(gate.maxAngleDegrees * Math.PI / 180) - 1e-6) continue;
      }
      const edge = Math.hypot(...[0, 1, 2].map((axis) => points[id * 3 + axis] - points[next * 3 + axis]));
      const candidate = distance.get(id)! + edge;
      if (candidate <= radiusMm && candidate < (distance.get(next) ?? Infinity)) { distance.set(next, candidate); pending.push(next); }
    }
  }
  return result;
}

/** Area-weighted native normals, without altering a vertex or polygon index. */
export function pointNormals(points: ArrayLike<number>, polys: ArrayLike<number>) {
  const normals = new Float32Array(points.length);
  for (let offset = 0; offset < polys.length;) {
    const count = polys[offset++]; const face = Array.from({ length: count }, () => polys[offset++]);
    for (let index = 1; index + 1 < face.length; index++) {
      const [a, b, c] = [face[0], face[index], face[index + 1]];
      const u = [0,1,2].map(axis => points[b * 3 + axis] - points[a * 3 + axis]);
      const v = [0,1,2].map(axis => points[c * 3 + axis] - points[a * 3 + axis]);
      const n = [u[1]*v[2]-u[2]*v[1], u[2]*v[0]-u[0]*v[2], u[0]*v[1]-u[1]*v[0]];
      for (const vertex of [a,b,c]) for (let axis = 0; axis < 3; axis++) normals[vertex*3+axis] += n[axis];
    }
  }
  for (let vertex = 0; vertex < points.length / 3; vertex++) {
    const length = Math.hypot(normals[vertex*3], normals[vertex*3+1], normals[vertex*3+2]);
    if (length) for (let axis = 0; axis < 3; axis++) normals[vertex*3+axis] /= length;
  }
  return normals;
}
